import { Injectable } from '@angular/core'
import { HttpParams, HttpClient, HttpHeaders } from '@angular/common/http'
import { Observable } from 'rxjs'
import { IGetRowsParams } from 'ag-grid-community'
import { map } from 'rxjs/operators'

export class PagedResult<T> {
  public data: T[]
  public skipped: number
  public rowCount: number

  public getRows(params: IGetRowsParams) {
    params.successCallback(this.data, this.rowCount - this.skipped)
  }
}

@Injectable()
export class TransformationService<T> {
  public requestTransform(data: T, method: string): T {
    return data
  }
  public responseTransform(response: any, method: string): T {
    return response as T
  }
  public responseTransformArray(response: any, method: string): T[] {
    return response as T[]
  }
  public responseTransformQuery(response: PagedResult<T>): PagedResult<T> {
    return response
  }
}

export class ODataFunction {
  public key: string | number
  public name: string
}

export abstract class ODataOperation<T> {
  private expandValue: string
  private selectValue: string

  constructor(
    protected typeName: string,
    protected config: ODataConfigurationBase,
    protected http: HttpClient,
    protected transformations: TransformationService<T> = null
  ) {}

  public expand(expandValue: string | string[]) {
    this.expandValue = this.parseStringOrStringArray(expandValue)
    return this
  }

  public select(selectValue: string | string[]) {
    this.selectValue = this.parseStringOrStringArray(selectValue)
    return this
  }

  protected getParams(): HttpParams {
    let params = new HttpParams()
    if (this.selectValue && this.selectValue.length > 0) {
      params = params.set(this.config.keys.select, this.selectValue)
    }
    if (this.expandValue && this.expandValue.length > 0) {
      params = params.set(this.config.keys.expand, this.expandValue)
    }
    return params
  }

  protected getTypeUri(): string {
    return this.config.getTypeUri(this.typeName)
  }

  protected getEntityUri(key: number | string): string {
    return this.config.getEntityUri(key, this.typeName)
  }

  protected getRequestOptions() {
    const options = this.config.requestOptions
    options['params'] = this.getParams()
    return options
  }

  protected getPostRequestOptions() {
    return {
      headers: this.config.postRequestHeaders
    }
  }

  protected getPutRequestOptions() {
    return {
      headers: this.config.putRequestHeaders
    }
  }

  protected getPatchRequestOptions() {
    return {
      headers: this.config.patchRequestHeaders
    }
  }

  protected getDeleteRequestOptions() {
    return {
      headers: this.config.deleteRequestHeaders
    }
  }

  abstract exec(): Observable<any>

  protected processRequestTransformations(entity: T, method: string): T {
    if (this.transformations) {
      return this.transformations.requestTransform(entity, method)
    }
    return entity
  }

  protected processResponseTransformations(entity: T, method: string): T {
    if (this.transformations) {
      return this.transformations.responseTransform(entity, method)
    }
    return entity
  }

  protected parseStringOrStringArray(input: string | string[]): string {
    if (input instanceof Array) {
      return input.join(',')
    }

    return input as string
  }
}

export abstract class OperationWithKey<T> extends ODataOperation<T> {
  constructor(
    protected typeName: string,
    protected config: ODataConfigurationBase,
    protected http: HttpClient,
    protected key: number | string,
    protected transformations: TransformationService<T>
  ) {
    super(typeName, config, http)
  }
  abstract exec(...args): Observable<any>
}

export abstract class OperationWithEntity<T> extends ODataOperation<T> {
  constructor(
    protected typeName: string,
    protected config: ODataConfigurationBase,
    protected http: HttpClient,
    protected entity: T,
    protected transformations: TransformationService<T>
  ) {
    super(typeName, config, http)
  }
  abstract exec(...args): Observable<any>
}

export abstract class OperationWithKeyAndEntity<T> extends ODataOperation<T> {
  constructor(
    protected typeName: string,
    protected config: ODataConfigurationBase,
    protected http: HttpClient,
    protected key: number | string,
    protected entity: T,
    protected transformations: TransformationService<T>
  ) {
    super(typeName, config, http)
  }
  abstract exec(...args): Observable<any>
}

export class GetOperation<T> extends OperationWithKey<T> {
  public exec(): Observable<T> {
    return this.http
      .get<T>(this.getEntityUri(this.key), this.getRequestOptions())
      .pipe(map(response => this.processResponseTransformations(response as T, 'GET')))
  }
}

export class PostOperation<T> extends OperationWithEntity<T> {
  public exec(): Observable<T> {
    const entity = (this.entity = this.processRequestTransformations(this.entity, 'POST'))
    return this.http
      .post<T>(this.getTypeUri(), entity, this.getPostRequestOptions())
      .pipe(map(response => this.processResponseTransformations(response as T, 'POST')))
  }
}

export class PatchOperation<T> extends OperationWithKeyAndEntity<T> {
  public exec(): Observable<T> {
    const entity = (this.entity = this.processRequestTransformations(this.entity, 'PATCH'))
    return this.http.patch<T>(this.getEntityUri(this.key), entity, this.getPatchRequestOptions())
  }
}

export class PutOperation<T> extends OperationWithKeyAndEntity<T> {
  public exec(): Observable<T> {
    const entity = (this.entity = this.processRequestTransformations(this.entity, 'PUT'))
    return this.http.put<T>(this.getEntityUri(this.key), entity, this.getPutRequestOptions())
  }
}

export class DeleteOperation<T> extends OperationWithKey<T> {
  public exec() {
    return this.http.delete<T>(this.getEntityUri(this.key), this.getDeleteRequestOptions())
  }
}

export class ODataQuery<T> extends ODataOperation<T> {
  private _filter: string
  private _top: number
  private _skip: number
  private _orderBy: string
  private _functionInfo: ODataFunction
  private _customParams: { name: string; value: string }[]

  constructor(
    _typeName: string,
    config: ODataConfigurationBase,
    http: HttpClient,
    functionInfo: ODataFunction,
    transformations: TransformationService<T> = null,
    customParams: { name: string; value: string }[]
  ) {
    super(_typeName, config, http, transformations)
    this._functionInfo = functionInfo
    this._customParams = customParams
  }

  public filter(filter: string): ODataQuery<T> {
    this._filter = filter
    return this
  }

  public top(top: number): ODataQuery<T> {
    this._top = top
    return this
  }

  public skip(skip: number): ODataQuery<T> {
    this._skip = skip
    return this
  }

  public orderBy(orderBy: string): ODataQuery<T> {
    this._orderBy = orderBy
    return this
  }

  public execAll(): Observable<T[]> {
    const params = this.getQueryParams()
    return this.http.get<T[]>(this.buildResourceURL(), { params })
  }

  protected processResponseTransformationsQuery(results: PagedResult<T>): PagedResult<T> {
    if (this.transformations) {
      return this.transformations.responseTransformQuery(results)
    }
    return results
  }

  public exec(): Observable<PagedResult<T>> {
    const config = this.config
    let params = this.getQueryParams()
    params = params.set('$count', 'true')
    const options = config.requestOptions
    options['params'] = params

    return this.http.get<PagedResult<T>>(this.buildResourceURL(), options).pipe(
      map((odataResult: any) => {
        const results = {
          data: odataResult.value,
          rowCount: odataResult['@odata.count']
        } as PagedResult<T>
        return this.processResponseTransformationsQuery(results)
      })
    )
  }

  private buildResourceURL(): string {
    if (this.typeName === 'PatientEpisodeSearch') {
      return `${this.config.restApi}/search/patient`
    }

    const key = this._functionInfo && this._functionInfo.key ? `(${this._functionInfo.key})` : ''
    const fn = this._functionInfo ? `${this._functionInfo.name}` : ''
    return `${this.config.baseUrl}/${this.typeName}${key}/${fn}`
  }

  private getQueryParams(): HttpParams {
    let params = super.getParams()

    if (this._customParams) {
      this._customParams.forEach(param => {
        params = params.set(param.name, param.value)
      })
    }

    if (this.typeName === 'PatientEpisodeSearch') {
      const firstParenIndex = this._functionInfo.name.indexOf('\'')
      const secondParenIndex = this._functionInfo.name.indexOf('\'', firstParenIndex + 1)
      const searchTerm = this._functionInfo.name.substring(firstParenIndex + 1, secondParenIndex)

      params = params.set('searchTerm', searchTerm)
    }

    if (this._filter) {
      params = params.set(this.config.keys.filter, this._filter)
    }
    if (this._top) {
      params = params.set(this.config.keys.top, this._top.toString())
    }
    if (this._skip) {
      params = params.set(this.config.keys.skip, this._skip.toString())
    }
    if (this._orderBy) {
      params = params.set(this.config.keys.orderBy, this._orderBy)
    }
    return params
  }
}

export class KeyConfigs {
  public filter = '$filter'
  public top = '$top'
  public skip = '$skip'
  public orderBy = '$orderby'
  public select = '$select'
  public expand = '$expand'
}

interface HttpOptions {
  headers: HttpHeaders
  body: any
}

@Injectable()
export class ODataConfigurationBase {
  public restApi: string
  public baseUrl: string
  public keys: KeyConfigs = new KeyConfigs()
  public baseHeaders: any = {
    'Content-Type': 'application/json; charset=utf-8',
    Accept: 'application/json'
  }

  public getTypeUri(typeName: string) {
    return this.baseUrl + '/' + typeName
  }

  public getEntityUri(entityKey: number | string, typeName: string) {
    if (typeof entityKey === 'string') {
      return this.getTypeUri(typeName) + (entityKey ? `('${entityKey}')` : '')
    } else {
      return this.getTypeUri(typeName) + (entityKey ? `(${entityKey})` : '')
    }
  }

  public handleError(err: any, caught: any): void {
    console.warn('OData error: ', err, caught)
  }

  get requestOptions(): HttpOptions {
    const headers = new HttpHeaders(this.baseHeaders)

    return { headers, body: '' }
  }

  get requestOptionsNoCache(): HttpOptions {
    const headers = this.baseHeaders
    headers['Cache-Control'] = 'max-age=0, no-cache, no-store, must-revalidate, private'
    const httpHeaders = new HttpHeaders(headers)

    return { headers: httpHeaders, body: '' }
  }

  getCustomHeaders(accept: string, contentType: string): HttpHeaders {
    const headerConfig: any = {}

    if (contentType) {
      headerConfig['Content-Type'] = contentType
    }

    if (accept) {
      headerConfig['Accept'] = accept
    }

    return new HttpHeaders(headerConfig)
  }

  get postRequestHeaders(): HttpHeaders {
    return new HttpHeaders(this.baseHeaders)
  }

  get putRequestHeaders(): HttpHeaders {
    const headers = this.baseHeaders
    headers['Prefer'] = 'return=representation'
    return new HttpHeaders(headers)
  }

  get patchRequestHeaders(): HttpHeaders {
    return this.putRequestHeaders
  }

  get deleteRequestHeaders(): HttpHeaders {
    return this.putRequestHeaders
  }

  public extractQueryResultDataWithCount<T>(res: T[], skip: number): PagedResult<T> {
    const pagedResult = new PagedResult<T>()

    pagedResult.data = res
    pagedResult.skipped = skip

    try {
      const count = parseInt(res['@odata.count'], 10) || res.length
      pagedResult.rowCount = count
    } catch (error) {
      console.warn('Cannot determine OData entities count. Falling back to collection length...')
      pagedResult.rowCount = res.length
    }

    return pagedResult
  }
}
